// Create Post Component
// Form for creating new posts

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Send, Loader2 } from 'lucide-react';
import { theme } from '../../styles/theme';
import { useChatRoomTheme, ChatRoomTheme } from '../../contexts/ChatRoomThemeContext';
import { checkLinkPolicy, roomRestrictsLinks } from '../../utils/communityLinkPolicy';
import { EmojiPicker } from './EmojiPicker';

const CreatePostContainer = styled.div<{ $theme: ChatRoomTheme }>`
  background: ${props => props.$theme === 'dark' ? '#0a0a0a' : '#f8f9fa'};
  min-height: 100vh;
  padding: 2rem 1.25rem;
  padding-bottom: calc(80px + 2rem);
  color: ${props => props.$theme === 'dark' ? 'white' : '#333'};
  transition: all 0.3s ease;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1rem;
    padding-bottom: calc(100px + 1rem);
  }
`;

const FormCard = styled(motion.div) <{ $theme: ChatRoomTheme }>`
  max-width: 800px;
  margin: 0 auto;
  background: ${props => props.$theme === 'dark'
    ? 'rgba(30, 30, 30, 0.6)'
    : 'rgba(255, 255, 255, 0.9)'};
  backdrop-filter: blur(20px);
  border: 1px solid ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(255, 140, 66, 0.1)'};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: ${props => props.$theme === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(255, 140, 66, 0.1)'};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const FormHeader = styled.div`
  display: flex;
  /* Was space-between, which pushed a theme toggle out to the right edge.
     Appearance is a Settings concern, not something to decide again while
     writing a post, so the header is now just the way back and the title. */
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2<{ $theme: ChatRoomTheme }>`
  font-size: 1.8rem;
  font-weight: 700;
  background: ${theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label<{ $theme: ChatRoomTheme }>`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.$theme === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)'};
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

const TitleInput = styled.input<{ $theme: ChatRoomTheme }>`
  width: 100%;
  background: ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'white'};
  border: 1px solid ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 12px;
  padding: 1rem;
  color: ${props => props.$theme === 'dark' ? 'white' : '#333'};
  font-size: 1.1rem;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.4)'
    : 'rgba(0, 0, 0, 0.4)'};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 4px ${props => props.$theme === 'dark'
    ? 'rgba(255, 140, 66, 0.1)'
    : 'rgba(255, 140, 66, 0.05)'};
    background: ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.08)'
    : 'white'};
  }
`;

const BodyTextarea = styled.textarea<{ $theme: ChatRoomTheme }>`
  width: 100%;
  min-height: 250px;
  background: ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'white'};
  border: 1px solid ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 12px;
  padding: 1rem;
  color: ${props => props.$theme === 'dark' ? 'white' : '#333'};
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  line-height: 1.6;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.4)'
    : 'rgba(0, 0, 0, 0.4)'};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 4px ${props => props.$theme === 'dark'
    ? 'rgba(255, 140, 66, 0.1)'
    : 'rgba(255, 140, 66, 0.05)'};
    background: ${props => props.$theme === 'dark'
    ? 'rgba(255, 255, 255, 0.08)'
    : 'white'};
  }
`;

const CharCount = styled.div<{ $isOverLimit?: boolean; $theme: ChatRoomTheme }>`
  font-size: 0.75rem;
  color: ${({ $isOverLimit, $theme }) => {
    if ($isOverLimit) return theme.colors.error;
    return $theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)';
  }};
  margin-top: 0.5rem;
  text-align: right;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  align-items: center;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary'; $theme: ChatRoomTheme }>`
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${({ $variant, $theme }) =>
    $variant === 'primary'
      ? `
    background: linear-gradient(135deg, #ff8c42 0%, #ff6b9d 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(255, 140, 66, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  `
      : `
    background: transparent;
    border: 1px solid ${$theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
    color: ${$theme === 'dark' ? 'white' : '#333'};

    &:hover {
      background: ${$theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button<{ $theme: ChatRoomTheme }>`
  background: transparent;
  border: 1px solid ${props => props.$theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  color: ${props => props.$theme === 'dark' ? 'white' : '#333'};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.$theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    transform: translateX(-2px);
  }
`;

const PolicyNotice = styled.div<{ $theme: ChatRoomTheme; $variant: 'hint' | 'error' }>`
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.45;
  border: 1px solid ${props => props.$variant === 'error'
    ? 'rgba(220, 38, 38, 0.45)'
    : props.$theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 140, 66, 0.25)'};
  background: ${props => props.$variant === 'error'
    ? 'rgba(220, 38, 38, 0.12)'
    : props.$theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 140, 66, 0.07)'};
  color: ${props => props.$variant === 'error'
    ? '#ef4444'
    : props.$theme === 'dark' ? 'rgba(255, 255, 255, 0.75)' : '#6b6b6b'};
`;

const ComposerTools = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const ComposerHint = styled.span<{ $theme: ChatRoomTheme }>`
  font-size: 0.8rem;
  opacity: 0.65;
  color: ${props => props.$theme === 'dark' ? 'rgba(255,255,255,0.8)' : '#666'};
  flex: 1 1 auto;
`;

interface CreatePostProps {
  communityId: string;
  onSubmit: (title: string, body: string) => Promise<void>;
  onCancel: () => void;
}

export const CreatePost: React.FC<CreatePostProps> = ({
  communityId,
  onSubmit,
  onCancel,
}) => {
  const { theme } = useChatRoomTheme();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Inserted at the caret rather than appended, so picking an emoji mid-sentence
  // puts it where the writer is looking. The caret is restored after it.
  const insertEmoji = (emoji: string) => {
    const textarea = bodyRef.current;
    if (!textarea) {
      setBody((prev) => prev + emoji);
      return;
    }
    const start = textarea.selectionStart ?? body.length;
    const end = textarea.selectionEnd ?? body.length;
    const next = body.slice(0, start) + emoji + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      textarea.focus();
      const caret = start + emoji.length;
      textarea.setSelectionRange(caret, caret);
    });
  };

  const TITLE_MAX_LENGTH = 300;
  const BODY_MAX_LENGTH = 10000;

  // communityId is the room's slug, which is what the link rules are keyed on.
  const restrictsLinks = roomRestrictsLinks(communityId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    // Checked here as a courtesy so the author is told before the round trip;
    // the server checks it again and its answer is the one that counts.
    const violation = checkLinkPolicy(communityId, `${title}\n${body}`);
    if (violation) {
      setError(violation);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit(title.trim(), body.trim());
    } catch (err: any) {
      // Shown, not just logged. This used to swallow the failure, so a
      // rejected post looked like a button that did nothing.
      console.error('Error creating post:', err);
      setError(err?.message || 'Could not create the post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CreatePostContainer $theme={theme}>
      <FormCard
        $theme={theme}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <FormHeader>
          <BackButton $theme={theme} onClick={onCancel}>
            ← Back
          </BackButton>
          <FormTitle $theme={theme}>Create Post</FormTitle>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label $theme={theme} htmlFor="title">Title</Label>
            <TitleInput
              $theme={theme}
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your post a catchy title..."
              maxLength={TITLE_MAX_LENGTH}
              autoFocus
            />
            <CharCount $isOverLimit={title.length > TITLE_MAX_LENGTH} $theme={theme}>
              {title.length} / {TITLE_MAX_LENGTH}
            </CharCount>
          </FormGroup>

          <FormGroup>
            <Label $theme={theme} htmlFor="body">Content</Label>
            <BodyTextarea
              ref={bodyRef}
              $theme={theme}
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Share your thoughts, stories, or questions... paste a GIF link to post a GIF"
              maxLength={BODY_MAX_LENGTH}
            />
            <ComposerTools>
              <EmojiPicker theme={theme} onSelect={insertEmoji} label="Add an emoji to your post" />
              <ComposerHint $theme={theme}>
                Paste a GIF link (Giphy, Tenor, or any .gif URL) and it posts as a GIF.
              </ComposerHint>
              <CharCount $isOverLimit={body.length > BODY_MAX_LENGTH} $theme={theme}>
                {body.length} / {BODY_MAX_LENGTH}
              </CharCount>
            </ComposerTools>
          </FormGroup>

          {restrictsLinks && !error && (
            <PolicyNotice $theme={theme} $variant="hint">
              Links in this room must point to LinkedIn — share the LinkedIn job posting or
              profile. Other links will not be accepted.
            </PolicyNotice>
          )}

          {error && (
            <PolicyNotice $theme={theme} $variant="error" role="alert">
              {error}
            </PolicyNotice>
          )}

          <ButtonGroup>
            <Button type="button" $variant="secondary" $theme={theme} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              $variant="primary"
              $theme={theme}
              disabled={isSubmitting || !title.trim()}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Post
                </>
              )}
            </Button>
          </ButtonGroup>
        </form>
      </FormCard>
    </CreatePostContainer>
  );
};
